import {
    APP_INITIALIZER,
    ErrorHandler,
    LOCALE_ID,
    enableProdMode,
    importProvidersFrom,
    provideZoneChangeDetection
} from '@angular/core';

import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, TitleStrategy, provideRouter, withHashLocation, withRouterConfig } from '@angular/router';
import { AuthHttpInterceptor, provideAuth0 } from '@auth0/auth0-angular';
import { CorrelationIdInterceptor } from '@aya/common/interceptors';
import { Loader } from '@googlemaps/js-api-loader';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ICON_SETTINGS, IconSettingsService, IconsModule } from '@progress/kendo-angular-icons';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { AppComponent } from 'src/app/app.component';
import { appRoutes } from 'src/app/app.routes';
import { NSwagMigrationInterceptor } from 'src/app/compliance/api/migration/nswag-migration.interceptor';
import { API_BASE_URL } from 'src/app/compliance/api/misc/api-base-url.token';
import { AppSettings } from 'src/app/config/app-settings';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { AppIdInterceptor } from 'src/app/core/app-id-interceptor';
import { IdentityService } from 'src/app/core/identity.service';
import { NovaKendoIconsService } from 'src/app/core/nova-icons.service';
import { reducers } from 'src/app/core/router.reducer';
import { ApiCacheInterceptor } from 'src/app/facility-contracts/shared/interceptors/api-cache.interceptor';
import { MasterDetailRouteReuseStrategy } from 'src/app/master-detail-route-reuse-strategy';
import { CustomTitleStrategy } from 'src/app/page-title/custom-title-strategy';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { NovaErrorHandler } from 'src/app/shared/services/nova-error-handler';
import { ENV, getEnv } from 'src/environments/environment.provider';
import { environment } from './environments/environment';
import { provideEffects } from '@ngrx/effects';
import { CoreEffects } from 'src/app/core/effects/core.effects';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { NgChartsModule } from 'ng2-charts';
import { SharedModule } from 'src/app/shared/shared.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRootStore } from 'src/app/root-store/root-store.providers';
import { NOVA_MATERIAL_PROVIDERS } from 'src/app/nova-material-defaults.const';

const initApplication =
    (ldFeatureManager: LDFeatureManager, settings: Settings, logger: LoggerService, identityService: IdentityService) =>
    async () => {
        await logger.init();

        // Load Google API via the JS library loader
        // See here: https://developers.google.com/maps/documentation/javascript/overview#js_api_loader_package
        const loader = new Loader({
            apiKey: settings.googleMapsKey,
            libraries: ['places'],
            version: 'weekly'
        });

        await loader.importLibrary('maps');
        await ldFeatureManager.init();
        identityService.init();
    };

if (environment.production) {
    enableProdMode();
}

const INTERCEPTORS = [
    { provide: HTTP_INTERCEPTORS, useClass: CorrelationIdInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AppIdInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NSwagMigrationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true }
];

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: false }),
        provideEnvironmentNgxMask(),
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(appRoutes, withHashLocation(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
        provideAuth0(),
        provideStore(reducers, {
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: false,
                strictActionWithinNgZone: true,
                strictActionTypeUniqueness: true
            }
        }),
        provideRouterStore(),
        provideStoreDevtools({
            name: 'Nova DevTools',
            logOnly: environment.production,
            connectInZone: true
        }),
        provideRootStore(),
        provideEffects(CoreEffects),
        ...INTERCEPTORS,
        ...NOVA_MATERIAL_PROVIDERS,
        importProvidersFrom(
            // angular deps
            BrowserModule,
            BrowserAnimationsModule,
            // nova modules
            SharedModule,
            IconsModule,
            MatIconModule,
            MatListModule,
            NgChartsModule
        ),
        { provide: APP_CONFIG, useValue: AppSettings },
        { provide: ENV, useFactory: getEnv },
        {
            provide: APP_INITIALIZER,
            useFactory: initApplication,
            multi: true,
            deps: [LDFeatureManager, APP_CONFIG, LoggerService, IdentityService]
        },
        { provide: ErrorHandler, useClass: NovaErrorHandler },
        {
            provide: API_BASE_URL,
            useFactory: (settings: Settings) => settings.coreAPIUrl?.replace(/\/$/, ''),
            multi: true,
            deps: [APP_CONFIG]
        },
        { provide: LOCALE_ID, useValue: 'en-US' },
        {
            provide: ICON_SETTINGS,
            useValue: {
                type: 'svg'
            }
        },
        {
            provide: IconSettingsService,
            useClass: NovaKendoIconsService
        },
        { provide: RouteReuseStrategy, useClass: MasterDetailRouteReuseStrategy },
        { provide: TitleStrategy, useClass: CustomTitleStrategy }
    ]
}).catch((err) => console.error(err));
